import { defaults } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { matchI } from 'ts-adt';
import {
  APPS_MAX_HEIGHT,
  APPS_MAX_WIDTH,
  APPS_MIN_HEIGHT,
  APPS_MIN_WIDTH,
  MARIO_DEFAULT_HEIGHT,
  MARIO_DEFAULT_WIDTH,
  CollaborativeApps,
} from '../../iframe/additional-apps.utils';
import { FrameItem, MarioResource } from '../../../../models/session';
import { environment } from '../../../../../environments/environment';
import { BoardItemConfig, CreationContext } from './board-item-configurations';

export const getMarioAppInstanceId = (state): CollaborativeApps => {
  if (state.appInstanceId) {
    return state.appInstanceId;
  }

  const resource = state?.resource as MarioResource | undefined;
  if (!resource) {
    return CollaborativeApps.MARIO;
  }
  return matchI(resource)({
    DEPLOYED: (deployed) => (deployed.subpath ? deployed.subpath.subpathName : deployed.name),
    DEV_URL: () => CollaborativeApps.MARIO,
    GENERATED: () => CollaborativeApps.MARIO,
  }) as CollaborativeApps;
};

export const getMarioAppUrl = (resource: MarioResource | undefined, start_url: string) => {
  if (!resource) {
    resource = { _type: 'DEV_URL', url: start_url };
  }

  const env = environment.production ? 'PROD' : 'DEV';

  return matchI(resource)({
    DEV_URL: ({ url }) => url,
    GENERATED: ({ appId, contentHash }) =>
      // Served cross-origin by the mario-runner from GCS.
      `${environment.marioRunnerBaseUrl}/${appId}?v=${contentHash}`,
    DEPLOYED: ({ name, major_version, subpath }) =>
      `https://pencil-${name}-${major_version}-${env}.web.app/${
        subpath ? subpath.subpathRoute : ''
      }`,
  });
};

export class BoardMarioConfigurations extends BoardItemConfig {
  resource: MarioResource;
  contentId: string;
  isPrivate: boolean;
  interactionsLocked: boolean;
  appInstanceId: string;
  marioId: string;
  title: string;
  url: string;

  static defaultBoardItemConfigurations: Partial<BoardItemConfig> = {
    minWidth: APPS_MIN_WIDTH,
    maxWidth: APPS_MAX_WIDTH,
    minHeight: APPS_MIN_HEIGHT,
    maxHeight: APPS_MAX_HEIGHT,
  };

  constructor(
    configurations: Partial<BoardMarioConfigurations> & Partial<BoardItemConfig>,
    globalCreationParams: CreationContext,
  ) {
    const marioAppName =
      configurations.appInstanceId ?? getMarioAppInstanceId(configurations.resource);

    // if width, height, name, url, or resource not provided, try to set them from the app configurations
    const app = globalCreationParams.appConfigurationService.getAppByUid(marioAppName);
    if (app) {
      configurations.height = configurations.height ?? app.height ?? MARIO_DEFAULT_HEIGHT;
      configurations.width = configurations.width ?? app.width ?? MARIO_DEFAULT_WIDTH;
      configurations.resource = configurations.resource ?? app.marioAppConfig?.mario_resource;
    }

    // GENERATED apps carry their own display title on the resource (sourced from
    // the app's <title> at publish, agent-overridable). Persisted in mario_state.resource,
    // so it survives reload — prefer it over the generic "Mario App" app-config title.
    const resource = configurations.resource;
    const generatedTitle = resource && resource._type === 'GENERATED' ? resource.title : undefined;
    configurations.title = configurations.title ?? generatedTitle ?? app?.title;

    // The URL comes from the item's own resource, so build it even when this viewer's app
    // list doesn't include the app (e.g. a library app that isn't assigned to their
    // institution, or a board copied into another institution). Otherwise the item gets
    // an empty URL and doesn't render.
    if (resource?._type || configurations.url) {
      configurations.url = getMarioAppUrl(resource, configurations.url ?? '');
    }

    super(
      {
        ...BoardMarioConfigurations.defaultBoardItemConfigurations,
        ...configurations,
      },
      globalCreationParams,
    );

    const defaultMarioConfigurations = {
      contentId: uuidv4(),
      isPrivate: false,
      interactionsLocked: false,
      marioId: uuidv4(),
      appInstanceId: CollaborativeApps.MARIO,
      resource: {},
      title: '',
      url: '',
    };

    const marioConfigurations = defaults(configurations, defaultMarioConfigurations);

    this.contentId = marioConfigurations.contentId;
    this.isPrivate = marioConfigurations.isPrivate;
    this.interactionsLocked = marioConfigurations.interactionsLocked;
    this.appInstanceId = marioConfigurations.appInstanceId;
    this.marioId = marioConfigurations.marioId;
    this.resource = marioConfigurations.resource;
    this.title = marioConfigurations.title;
    this.url = marioConfigurations.url;
  }

  static fromYCanvasItemObject(
    frameItem: FrameItem,
    globalCreationsParams: CreationContext,
  ): BoardMarioConfigurations {
    const configurations = BoardItemConfig.fromYCanvasItemObject(
      frameItem,
      globalCreationsParams,
      BoardMarioConfigurations.defaultBoardItemConfigurations,
    );

    configurations.ownerId = configurations.ownerId ?? frameItem.mario_state?.ownerId;

    return new BoardMarioConfigurations(
      {
        ...configurations,
        resource: frameItem.mario_state?.resource,
        url: frameItem.mario_state?.url,
        contentId: frameItem.content_id,
        isPrivate: frameItem.isPrivate,
        interactionsLocked: frameItem.interactionsLocked,
        marioId: frameItem.mario_state?.id,
        appInstanceId: getMarioAppInstanceId(frameItem.mario_state),
      },
      globalCreationsParams,
    );
  }
}
